import type { ReactNode } from 'react';
import { formatPlainDate } from '../../core/dates/plain-date';
import { useTranslation } from '../../core/hooks/use-translation';
import type { PortalCaseDetail } from '../../core/models/portal-case-detail';
import { Button } from '../../ui/button';
import { ArrowRightIcon, CaseIcon, CheckIcon, ClockIcon, SignatureIcon } from '../../ui/portal-icons';
import type { Tone } from '../../ui/tone';

const TONES: Record<Tone, { band: string; icon: string }> = {
    neutral: {
        band: 'bg-slate-50 dark:bg-slate-950/40',
        icon: 'bg-slate-200 text-slate-600 dark:bg-slate-800 dark:text-slate-300'
    },
    brand: {
        band: 'bg-brand-50 dark:bg-brand-950/60',
        icon: 'bg-brand-100 text-brand-700 dark:bg-brand-900 dark:text-brand-300'
    },
    positive: {
        band: 'bg-slate-50 dark:bg-slate-950/40',
        icon: 'bg-emerald-100 text-emerald-700 dark:bg-emerald-900 dark:text-emerald-300'
    },
    warning: {
        band: 'bg-amber-50 dark:bg-amber-950/60',
        icon: 'bg-amber-100 text-amber-700 dark:bg-amber-900 dark:text-amber-300'
    },
    critical: {
        band: 'bg-rose-50 dark:bg-rose-950/60',
        icon: 'bg-rose-100 text-rose-700 dark:bg-rose-900 dark:text-rose-300'
    }
};

/**
 * What the case needs from the customer now, as the foot of the case header. Exactly one state, in
 * priority order: closed, signing unknown, a signature waiting, the office's next step, or nothing.
 *
 * **"Nothing needed" is only said when it is known.** If the signing read failed, a pending
 * signature may exist, so the banner says the check failed instead of reassuring on a guess.
 *
 * **A signature outranks the next step without hiding it** — the office's written step stays as a
 * second line, because it is often what the signature is for.
 */
export function CaseStatusBanner({
    clientCase,
    pendingSignatures,
    onReviewSignatures
}: {
    clientCase: PortalCaseDetail;
    /** Null when the signing read failed. */
    pendingSignatures: number | null;
    onReviewSignatures: () => void;
}) {
    const { t, language } = useTranslation();

    const due =
        clientCase.nextActionDueOn === null ? null : (
            <span className="inline-flex items-center gap-1.5 font-medium text-amber-800 dark:text-amber-400">
                <ClockIcon className="size-4" />
                {t('portal.due')} {formatPlainDate(clientCase.nextActionDueOn, language)}
            </span>
        );

    const nextActionLine =
        clientCase.nextActionText === null ? null : (
            <span className="mt-2 flex flex-wrap items-center gap-x-3 gap-y-1 text-slate-700 dark:text-slate-300">
                <span>
                    <span className="font-semibold">{t('portal.next_action')}:</span> {clientCase.nextActionText}
                </span>
                {due}
            </span>
        );

    if (clientCase.isClosed) {
        return (
            <Band tone="neutral" icon={<CaseIcon className="size-5" />} title={t('portal.case_closed_title')}>
                {t('portal.case_closed_hint')}
            </Band>
        );
    }

    if (pendingSignatures === null) {
        return (
            <Band
                tone="critical"
                icon={<SignatureIcon className="size-5" />}
                title={t('portal.signatures_failed_title')}
            >
                {t('portal.signatures_failed_hint')}
                {nextActionLine}
            </Band>
        );
    }

    if (pendingSignatures > 0) {
        return (
            <Band
                tone="warning"
                icon={<SignatureIcon className="size-5" />}
                title={t('portal.signature_needed_title')}
                action={
                    <Button onClick={onReviewSignatures}>
                        {t('portal.signature_needed_action')}
                        <ArrowRightIcon className="size-4" />
                    </Button>
                }
            >
                {t('portal.signature_needed_hint').replace('{count}', String(pendingSignatures))}
                {nextActionLine}
            </Band>
        );
    }

    if (clientCase.nextActionText !== null) {
        return (
            <Band
                tone="brand"
                icon={<ArrowRightIcon className="size-5" />}
                title={
                    <>
                        <span className="block text-xs font-semibold tracking-wider text-brand-800 uppercase dark:text-brand-300">
                            {t('portal.next_action')}
                        </span>
                        <span className="mt-0.5 block text-lg">{clientCase.nextActionText}</span>
                    </>
                }
            >
                {due}
            </Band>
        );
    }

    return (
        <Band tone="positive" icon={<CheckIcon className="size-5" />} title={t('portal.nothing_needed')}>
            {t('portal.nothing_needed_hint')}
        </Band>
    );
}

function Band({
    tone,
    icon,
    title,
    action,
    children
}: {
    tone: Tone;
    icon: ReactNode;
    title: ReactNode;
    action?: ReactNode;
    children?: ReactNode;
}) {
    return (
        <section
            className={`flex flex-wrap items-center gap-x-4 gap-y-3 border-t border-slate-200/80 px-5 py-4 sm:px-7 sm:py-5 dark:border-slate-800 ${TONES[tone].band}`}
        >
            <span
                aria-hidden="true"
                className={`flex size-11 shrink-0 items-center justify-center self-start rounded-xl ${TONES[tone].icon}`}
            >
                {icon}
            </span>

            <div className="min-w-0 flex-1 basis-56">
                <h2 className="font-bold text-slate-900 dark:text-slate-100">{title}</h2>
                {children !== null && children !== undefined && (
                    <p className="mt-1 flex flex-col text-sm text-slate-600 dark:text-slate-400">{children}</p>
                )}
            </div>

            {action !== undefined && <div className="w-full sm:w-auto [&>button]:w-full">{action}</div>}
        </section>
    );
}
